import {defineStore, storeToRefs} from 'pinia';
import {inject, ref, useAuth, useTranslation} from '#imports';
import {useErrorHandler} from '@malt/utils';
import type {FetchError} from '@api';
import {RegisteredRepositories} from '~/config/application.services';
import {useProfileStore} from '~/store/profileStore';
import type {Appraisal, Appraisals} from '~/types/domain/Appraisals';

export const useAppraisalStore = defineStore('appraisals', () => {
    const profileRepository = inject(RegisteredRepositories.injectionKeys.profile)!;
    const {profile} = storeToRefs(useProfileStore());
    const {user} = storeToRefs(useAuth());
    const {handleFetchError} = useErrorHandler();
    const {t} = useTranslation();

    const appraisals = ref<Appraisals>();

    async function getAppraisals(): Promise<Appraisals | undefined> {
        appraisals.value = await profileRepository.fetchAppraisals(profile.value!);
        return appraisals.value;
    }

    async function updateAppraisalVisibility(appraisal: Appraisal): Promise<void> {
        const isClientAppraisal = appraisal.clientAccountId && appraisal.clientAccountId === user.value?.id;
        const visibilityParam = isClientAppraisal ? appraisal.hiddenByClient : appraisal.hidden;
        try {
            await profileRepository.updateAppraisalVisibility(appraisal, visibilityParam);
            if (isClientAppraisal) {
                appraisal.hiddenByClient = !appraisal.hiddenByClient;
            } else {
                appraisal.hidden = !appraisal.hidden;
            }
        } catch (e) {
            handleFetchError(e as FetchError, {customErrorMessage: t('profile.section.appraisals.toggle.visibility.error')});
            throw e;
        }
    }

    function setAppraisals(newAppraisals: Appraisals | undefined) {
        appraisals.value = newAppraisals;
    }

    return {
        appraisals,
        getAppraisals,
        updateAppraisalVisibility,
        setAppraisals,
    };
});
